import { Scene, Vector3 } from 'three';
import Stats from 'three/addons/libs/stats.module.js';
import { Renderer } from './Renderer';
import { Loop } from './Loop';
import { SkyDome } from '../world/SkyDome';
import { createTerrain } from '../world/Terrain';
import { createScenery } from '../world/Scenery';
import { findSpawn, terrainHeight } from '../world/heightfield';
import { TARGET_SITES } from '../world/rangeLayout';
import { Tank } from '../entities/Tank';
import { Shells } from '../entities/Shells';
import { Targets } from '../entities/Targets';
import { Impacts } from '../entities/Impacts';
import { Sight, type Solution } from '../entities/Sight';
import { Input } from '../controls/Input';
import { TurretCamera } from '../controls/TurretCamera';
import { Hud } from '../ui/Hud';
import {
  MUZZLE_SPEED,
  GRAVITY,
  MAX_RANGE,
  aimDirection,
  flatRange,
  launchPitch,
} from '../utils/ballistics';

/**
 * Orchestrateur : construit le champ de tir, tient la machine a etats de la
 * partie et distribue le delta a chaque systeme. cf. doc/00-architecture.md
 */

type State = 'loading' | 'ready' | 'playing' | 'won';

/** Depart face a l'aval : les cibles sont de ce cote. */
const SPAWN_YAW = 0;

/** Duree de rechargement de la piece, en secondes. */
const RELOAD_TIME = 1.2;

export class Game {
  private readonly scene = new Scene();
  private readonly renderer: Renderer;
  private readonly input: Input;
  private readonly hud: Hud;
  private readonly loop: Loop;
  private readonly stats = new Stats();

  private sky!: SkyDome;
  private tank!: Tank;
  private shells!: Shells;
  private targets!: Targets;
  private impacts!: Impacts;
  private sight!: Sight;
  private camera!: TurretCamera;

  private readonly spawn = findSpawn();
  private state: State = 'loading';
  private runTime = 0;
  private reloadLeft = 0;
  private hits = 0;
  private lastShownSecond = -1;
  private frames = 0;

  /** Tampons reutilises chaque frame : rien ne s'alloue dans la boucle. */
  private readonly muzzle = new Vector3();
  private readonly aim = new Vector3();
  private readonly anchor = new Vector3();
  private readonly lead = new Vector3();
  private readonly layDir = new Vector3();
  private solution: Solution | null = null;

  /** Camera libre : uniquement en developpement, pour inspecter le char. */
  private freeCam: { offset: Vector3; look: Vector3 } | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = new Renderer(canvas);
    this.input = new Input(canvas);
    this.hud = new Hud(
      () => this.beginRun(),
      () => this.beginRun(),
    );
    this.loop = new Loop((dt) => this.frame(dt));

    this.stats.dom.style.cssText = 'position:fixed;bottom:16px;right:16px;display:none';
    document.body.appendChild(this.stats.dom);
    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyP') {
        this.stats.dom.style.display = this.stats.dom.style.display === 'none' ? 'block' : 'none';
      }
    });

    // Reprendre la souris apres un Echap, sans repasser par le menu.
    canvas.addEventListener('click', () => {
      if (this.state === 'playing' && !this.input.pointerLocked) {
        this.input.requestPointerLock();
      }
    });
  }

  async init(): Promise<void> {
    this.hud.setProgress(0.1, 'Nivellement du champ de tir…');
    this.sky = new SkyDome(this.scene, this.renderer.gl);

    // Un await laisse le navigateur peindre la barre de progression entre
    // deux etapes lourdes ; sans ca l'ecran reste figé.
    await nextFrame();
    this.hud.setProgress(0.3, 'Modelage du terrain…');
    await nextFrame();
    createTerrain(this.scene);

    this.hud.setProgress(0.62, 'Installation du décor…');
    await nextFrame();
    createScenery(this.scene);

    this.hud.setProgress(0.8, 'Mise en place des cibles…');
    await nextFrame();
    this.targets = new Targets(this.scene);
    this.impacts = new Impacts(this.scene);
    this.shells = new Shells(this.scene);
    this.sight = new Sight(this.scene);

    this.hud.setProgress(0.92, 'Mise en route du char…');
    await nextFrame();
    this.tank = new Tank(this.scene);
    this.tank.spawn(this.spawn, SPAWN_YAW);

    this.camera = new TurretCamera(this.renderer.camera, this.renderer);
    this.tank.turretAnchor(this.anchor);
    this.tank.muzzleDirection(this.aim);
    this.camera.snapTo(this.anchor, this.aim, this.tank.worldTurretYaw);

    this.exposeDevHooks();

    this.hud.setProgress(1, 'Prêt.');
    this.state = 'ready';
    this.hud.showStart();
    this.hud.setTargets(0, this.targets.total);
    this.hud.setShots(0, 0);
    this.hud.setReload(1);
    this.loop.start();
  }

  /**
   * En developpement seulement : permet de se placer n'importe ou sur le
   * champ de tir et de tirer depuis la console. C'est ce dont se sert
   * scripts/screenshot.mjs, parce que traverser 250 m au pas en rendu
   * logiciel prendrait des minutes.
   *
   *   range.teleport(0, -60)
   *   range.layGun(1)        // pointe sur la cible n°1 et tire
   */
  private exposeDevHooks(): void {
    if (!import.meta.env.DEV) return;
    Object.assign(window, {
      range: {
        teleport: (x: number, z: number, yaw = 0) => {
          this.tank.spawn(new Vector3(x, 0, z), yaw);
          this.snapCamera();
        },
        /** Pointe la tourelle et le canon sur des angles absolus (radians). */
        aim: (turretYaw: number, gunPitch: number) => {
          this.tank.layGun(turretYaw - this.tank.hullYaw, gunPitch);
          this.snapCamera();
          return { pitch: this.tank.worldPitch, yaw: this.tank.worldYaw };
        },
        /**
         * Resout le tir sur une cible, avance comprise, et l'execute.
         *
         * C'est le test qui compte : la hausse est calculee par
         * `launchPitch`, qui ne sait rien de la scene, et l'avance par
         * `Targets.predictCenter`. Si l'obus touche, c'est que la balistique
         * affichee, celle des obus et la position des cibles sont bien le
         * meme modele.
         *
         * Le temps de vol depend de la distance, qui depend de l'avance, qui
         * depend du temps de vol : l'equation est implicite, on l'itere. Trois
         * tours suffisent largement -- l'avance varie de quelques metres entre
         * la deuxieme et la troisieme.
         */
        layGun: (index: number) => {
          const target = this.targets.items[index];
          if (!target) return null;

          let flight = 0;
          let range = 0;
          let pitch: number | null = null;
          let yaw = 0;

          for (let iteration = 0; iteration < 4; iteration++) {
            // La bouche est relue a chaque tour : pointer le canon la deplace
            // de plusieurs metres, et la solution doit partir d'ou l'obus
            // partira vraiment.
            this.tank.muzzlePoint(this.muzzle);
            const point = this.targets.predictCenter(index, flight, this.lead);
            if (!point) return null;
            const dx = point.x - this.muzzle.x;
            const dz = point.z - this.muzzle.z;
            range = Math.hypot(dx, dz);
            pitch = launchPitch(range, point.y - this.muzzle.y);
            if (pitch === null) break;
            yaw = Math.atan2(dx, dz);
            // Temps de vol : la composante horizontale de la vitesse est
            // constante, donc t = portee / (v cos(hausse)).
            flight = range / (MUZZLE_SPEED * Math.cos(pitch));
            // Angles monde -> angles mecaniques, assiette de la caisse
            // comprise.
            this.tank.layGunWorld(aimDirection(yaw, pitch, this.layDir));
          }
          if (pitch === null) return { reachable: false, range };

          this.reloadLeft = 0;
          this.shoot();
          return {
            reachable: true,
            range,
            pitchDeg: (pitch * 180) / Math.PI,
            worldPitchDeg: (this.tank.worldPitch * 180) / Math.PI,
            flight,
            kind: target.site.kind,
          };
        },
        /**
         * Avance la simulation sans rendre une seule image.
         *
         * En rendu logiciel la boucle tourne a une image par seconde, et le
         * delta est borne a 1/20 s : le temps simule avance vingt fois moins
         * vite que le temps reel, et attendre les quatre secondes de vol d'un
         * obus en demande quatre-vingts. Ce hook fait tourner la physique --
         * obus, collisions, cibles -- sans toucher au rendu. Les tests
         * passent de vingt minutes a quelques secondes.
         */
        settle: (seconds: number) => {
          const dt = 1 / 60;
          const steps = Math.ceil(seconds / dt);
          for (let i = 0; i < steps; i++) {
            this.targets.update(dt);
            this.impacts.update(dt);
            this.shells.update(
              dt,
              (from, to) => this.targets.hitBy(from, to),
              (point, target, incoming) => this.onImpact(point, target, incoming),
            );
            if (this.shells.inFlight === 0) break;
          }
          return { inFlight: this.shells.inFlight, hits: this.hits };
        },
        /** Tire droit devant, sans toucher au pointage. */
        fire: () => {
          this.reloadLeft = 0;
          return this.shoot();
        },
        /** Detruit toutes les cibles sauf une : pour tester l'ecran de fin. */
        almostWin: () => {
          const left = this.targets.items
            .map((t, i) => ({ t, i }))
            .filter(({ t }) => !t.hit);
          for (const { i } of left.slice(1)) {
            if (this.targets.destroy(i)) this.hits++;
          }
          this.hud.setTargets(this.targets.destroyed, this.targets.total);
          return left[0]?.i ?? null;
        },
        /** Etat des cibles : position, portee, famille, touchee ou non. */
        targets: () =>
          this.targets.items.map((t) => ({
            kind: t.site.kind,
            range: Math.round(t.site.range),
            center: t.center.toArray().map((v) => Math.round(v * 10) / 10),
            radius: t.radius,
            hit: t.hit,
          })),
        /** Solution de tir courante, telle que le HUD l'affiche. */
        solution: () => this.solution && { ...this.solution },
        /** Constantes de balistique, pour verifier la portee theorique. */
        ballistics: () => ({
          muzzleSpeed: MUZZLE_SPEED,
          gravity: GRAVITY,
          maxRange: MAX_RANGE,
        }),
        /**
         * Les deux fonctions de balistique pure, exposees telles quelles.
         *
         * Elles permettent de tester l'invariant le plus net du projet sans
         * terrain, sans scene et sans rendu : resoudre la hausse pour une
         * portee, puis recalculer la portee de cette hausse, doit redonner la
         * portee de depart.
         */
        solvePitch: (range: number, rise = 0) => launchPitch(range, rise),
        flatRange: (pitch: number) => flatRange(pitch),
        /** Rapproche la camera de conduite (valeur relative, en metres). */
        zoom: (bias: number) => this.camera.setDistanceBias(bias),
        /**
         * Detache la camera et la place a `offset` du char, visant `look`
         * (relatif au char). `freeCam(null)` revient au suivi normal.
         */
        freeCam: (offset: number[] | null, look: number[] = [0, 2, 0]) => {
          this.freeCam = offset
            ? { offset: new Vector3(...offset), look: new Vector3(...look) }
            : null;
          if (!offset) this.snapCamera();
        },
        debug: () => ({
          fog: this.scene.fog && 'color' in this.scene.fog ? this.scene.fog.color.toArray() : null,
          zenith: SkyDome.zenith.toArray(),
          exposure: this.renderer.gl.toneMappingExposure,
          frames: this.frames,
          tank: this.tank.position.toArray().map((v) => Math.round(v * 10) / 10),
          hullYaw: this.tank.hullYaw,
          worldPitch: this.tank.worldPitch,
          gunPitch: this.tank.gunPitch,
          turretYaw: this.tank.turretYaw,
          input: {
            elevate: this.input.state.elevate,
            traverse: this.input.state.traverse,
            turn: this.input.state.turn,
            forward: this.input.state.forward,
            mousePitch: this.input.state.mousePitch,
            mouseYaw: this.input.state.mouseYaw,
            locked: this.input.pointerLocked,
          },
          shellsInFlight: this.shells.inFlight,
          shotsFired: this.shells.shotsFired,
          hits: this.hits,
        }),
        /** Altitude du terrain, pour verifier le champ de hauteur. */
        ground: (x: number, z: number) => terrainHeight(x, z),
        sites: () => TARGET_SITES.map((s) => ({ kind: s.kind, range: Math.round(s.range) })),
        state: () => this.state,
      },
    });
  }

  private snapCamera(): void {
    this.tank.turretAnchor(this.anchor);
    this.tank.muzzleDirection(this.aim);
    this.camera.snapTo(this.anchor, this.aim, this.tank.worldTurretYaw);
  }

  private beginRun(): void {
    this.targets.reset();
    this.shells.reset();
    this.impacts.reset();
    this.tank.spawn(this.spawn, SPAWN_YAW);
    this.snapCamera();
    this.input.reset();
    this.input.requestPointerLock();
    this.runTime = 0;
    this.reloadLeft = 0;
    this.hits = 0;
    this.lastShownSecond = -1;
    this.state = 'playing';
    this.hud.showPlaying();
    this.hud.setTargets(0, this.targets.total);
    this.hud.setShots(0, 0);
    this.hud.setReload(1);
  }

  /** Tente un tir. Renvoie false si la piece n'est pas chargee. */
  private shoot(): boolean {
    if (this.reloadLeft > 0) return false;
    // `fire` remplit le point de depart *puis* declenche le recul : lire la
    // bouche apres l'aurait reculee de 55 cm.
    this.tank.fire(this.muzzle, this.aim);
    if (!this.shells.fire(this.muzzle, this.aim)) return false;
    this.impacts.muzzleBlast(this.muzzle, this.aim);
    this.reloadLeft = RELOAD_TIME;
    this.hud.setShots(this.shells.shotsFired, this.hits);
    return true;
  }

  private frame(dt: number): void {
    this.stats.begin();
    this.frames++;

    // Le champ de tir vit meme sur l'ecran d'accueil : les cibles mobiles
    // vont et viennent, la fumee se dissipe.
    this.sky.update(dt, this.tank.position);
    this.targets.update(dt);
    this.impacts.update(dt);

    const playing = this.state === 'playing';
    const input = this.input.state;

    if (playing) {
      if (this.input.consumeFire() || (input.fire && this.reloadLeft <= 0)) this.shoot();
      this.tank.update(dt, input);
      this.reloadLeft = Math.max(0, this.reloadLeft - dt);
      this.runTime += dt;
    } else {
      // Hors partie le char reste vivant mais n'obeit plus aux commandes.
      this.tank.update(dt, NEUTRAL);
    }

    // Les obus volent dans tous les etats : un coup parti juste avant la
    // victoire doit arriver au but.
    this.shells.update(
      dt,
      (from, to) => this.targets.hitBy(from, to),
      (point, target, incoming) => this.onImpact(point, target, incoming),
    );

    this.updateAim(playing && !!input.sight);

    if (playing) {
      const second = Math.floor(this.runTime);
      if (second !== this.lastShownSecond) {
        this.lastShownSecond = second;
        this.hud.setTime(this.runTime);
      }
      this.hud.setReload(this.reloadLeft <= 0 ? 1 : 1 - this.reloadLeft / RELOAD_TIME);
      this.hud.setSightMode(!!input.sight);
    }

    if (this.freeCam) {
      this.renderer.camera.position.copy(this.tank.position).add(this.freeCam.offset);
      this.renderer.camera.lookAt(
        this.tank.position.x + this.freeCam.look.x,
        this.tank.position.y + this.freeCam.look.y,
        this.tank.position.z + this.freeCam.look.z,
      );
    } else {
      this.tank.turretAnchor(this.anchor);
      this.camera.update(
        dt,
        this.anchor,
        this.aim,
        this.solution ? this.sight.impactPoint : null,
        this.tank.worldTurretYaw,
        this.tank.speedRatio,
        playing && !!input.sight,
      );
    }

    this.renderer.update(dt);
    this.renderer.render(this.scene);
    this.input.endFrame();
    this.stats.end();
  }

  /** Recalcule la solution de tir et l'affichage d'aide a la visee. */
  private updateAim(sightMode: boolean): void {
    this.tank.muzzlePoint(this.muzzle);
    this.tank.muzzleDirection(this.aim);

    if (this.state !== 'playing') {
      this.sight.hide();
      this.solution = null;
      return;
    }

    this.solution = this.sight.update(
      this.muzzle,
      this.aim,
      (from, to) => this.targets.hitBy(from, to),
      sightMode,
    );
    this.hud.setSolution(this.solution, this.tank.worldPitch);
  }

  private onImpact(point: Vector3, target: number | null, incoming: Vector3): void {
    if (target === null) {
      this.impacts.ground(point, incoming);
      return;
    }
    this.impacts.target(point, incoming);
    if (!this.targets.destroy(target)) return;

    this.hits++;
    this.hud.setTargets(this.targets.destroyed, this.targets.total);
    this.hud.setShots(this.shells.shotsFired, this.hits);

    if (this.targets.done && this.state === 'playing') {
      this.state = 'won';
      this.hud.showWon(this.runTime, this.shells.shotsFired, this.hits);
      this.input.reset();
      this.input.exitPointerLock();
      this.sight.hide();
    }
  }
}

/** Commandes au neutre, pour les etats hors partie. */
const NEUTRAL = {
  forward: 0,
  turn: 0,
  traverse: 0,
  elevate: 0,
  mouseYaw: 0,
  mousePitch: 0,
  fire: false,
  sight: false,
} as const;

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}
